import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Heart, MessageCircle, ArrowRight, ArrowLeft } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

const posts = [
  {
    handle: "@Tirami.mx",
    img: "https://images.unsplash.com/photo-1655207358778-025cde9f8c64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBkZXNzZXJ0JTIwZGluaW5nJTIwbGlmZXN0eWxlfGVufDB8fHx8MTc3OTI0MzQ1NHww&ixlib=rb-4.1.0&q=85",
    caption: "Sunday ritual. Tirami delivers.",
    likes: "4.2k",
    comments: 128,
  },
  {
    handle: "@marco.ricci",
    img: "https://images.pexels.com/photos/35981910/pexels-photo-35981910.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    caption: "Best tiramisu in the city. Hands down.",
    likes: "2.8k",
    comments: 94,
  },
  {
    handle: "@aria.de.luca",
    img: "https://images.unsplash.com/photo-1767335911122-e996a8c3f06c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBkZXNzZXJ0JTIwZGluaW5nJTIwbGlmZXN0eWxlfGVufDB8fHx8MTc3OTI0MzQ1NHww&ixlib=rb-4.1.0&q=85",
    caption: "The matcha is a religious experience.",
    likes: "6.1k",
    comments: 211,
  },
  {
    handle: "@chef.lorenzo",
    img: "https://images.pexels.com/photos/14030940/pexels-photo-14030940.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    caption: "Texture. Restraint. Tirami knows.",
    likes: "9.4k",
    comments: 312,
  },
  {
    handle: "@nora.b",
    img: "https://images.unsplash.com/photo-1655207358778-025cde9f8c64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBkZXNzZXJ0JTIwZGluaW5nJTIwbGlmZXN0eWxlfGVufDB8fHx8MTc3OTI0MzQ1NHww&ixlib=rb-4.1.0&q=85",
    caption: "Layers within layers. Poetry.",
    likes: "3.5k",
    comments: 67,
  },
];

// Triple the posts for seamless looping
const loopedPosts = [...posts, ...posts, ...posts];

export default function SocialProof() {
  const { t } = useT();
  const trackRef = useRef(null);
  const autoRef = useRef({ paused: false, raf: 0 });

  // Auto-scroll loop
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const SPEED = 0.6; // px per frame
    let lastT = performance.now();

    const tick = (t) => {
      const dt = t - lastT;
      lastT = t;
      if (!autoRef.current.paused && el) {
        el.scrollLeft += SPEED * (dt / 16.67);
        // loop: when past 2/3 (end of second copy), jump back by 1/3
        const third = el.scrollWidth / 3;
        if (el.scrollLeft >= third * 2) {
          el.scrollLeft -= third;
        }
      }
      autoRef.current.raf = requestAnimationFrame(tick);
    };

    // Start in the middle copy for seamless rewind
    el.scrollLeft = el.scrollWidth / 3;
    autoRef.current.raf = requestAnimationFrame(tick);

    const pause = () => (autoRef.current.paused = true);
    const resume = () => (autoRef.current.paused = false);

    el.addEventListener("mouseenter", pause);
    el.addEventListener("mouseleave", resume);
    el.addEventListener("touchstart", pause, { passive: true });
    el.addEventListener("touchend", resume);

    return () => {
      cancelAnimationFrame(autoRef.current.raf);
      el.removeEventListener("mouseenter", pause);
      el.removeEventListener("mouseleave", resume);
      el.removeEventListener("touchstart", pause);
      el.removeEventListener("touchend", resume);
    };
  }, []);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const w = el.clientWidth * 0.7;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  };

  return (
    <section
      id="journal"
      data-testid="social-section"
      className="relative w-full bg-[#FFF8EE] py-28 md:py-40 overflow-hidden"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <p className="label-tracked text-[#7B5C4E]" data-testid="social-eyebrow">
              {t("social.eyebrow")}
            </p>
            <h2
              className="font-serif font-light text-[#211512] mt-6 leading-[1]"
              style={{ fontSize: "clamp(2.4rem, 5.6vw, 5rem)" }}
              data-testid="social-headline"
            >
              {t("social.headline_1")}
              <br />
              <span className="italic" style={{ color: "#7B5C4E" }}>
                {t("social.headline_2")}
              </span>
            </h2>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => scrollBy(-1)}
              data-testid="social-prev"
              className="w-12 h-12 border border-[#211512]/30 flex items-center justify-center hover:bg-[#211512] hover:text-[#FFF8EE] transition-all duration-300"
              aria-label="Previous"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              data-testid="social-next"
              className="w-12 h-12 border border-[#211512]/30 flex items-center justify-center hover:bg-[#211512] hover:text-[#FFF8EE] transition-all duration-300"
              aria-label="Next"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={trackRef}
        className="no-scrollbar mt-16 flex gap-6 overflow-x-auto px-6 md:px-12 scroll-smooth"
        data-testid="social-carousel"
        style={{ scrollBehavior: "auto" }}
      >
        {loopedPosts.map((p, i) => (
          <motion.article
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="group relative shrink-0 w-[78vw] sm:w-[46vw] md:w-[32vw] lg:w-[22vw] cursor-pointer"
            data-testid={`social-post-${i % posts.length}`}
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-[#211512]">
              <img
                src={p.img}
                alt={p.handle}
                className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              />

              <div
                className="absolute top-4 left-4 px-3 py-1.5 label-tracked text-[#FFF8EE]"
                style={{
                  background: "rgba(33,21,18,0.55)",
                  backdropFilter: "blur(6px)",
                }}
              >
                {p.handle}
              </div>

              <div className="absolute inset-0 bg-[#211512]/0 group-hover:bg-[#211512]/55 transition-all duration-500" />

              <div className="absolute inset-0 flex flex-col justify-end p-5 text-[#FFF8EE] opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                <p className="font-serif italic text-base md:text-lg leading-snug">
                  "{p.caption}"
                </p>
                <div className="flex items-center gap-5 mt-4 label-tracked">
                  <span className="inline-flex items-center gap-1.5">
                    <Heart className="w-3.5 h-3.5" /> {p.likes}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <MessageCircle className="w-3.5 h-3.5" /> {p.comments}
                  </span>
                </div>
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
